import { mapState, mapActions } from 'vuex'
import { obterMascaraCnpj } from '@/util/cnpjMask'
import mascaras from '@/util/mascaras'
import util from '@/util'
import mensagens from '@/util/mensagens'
import spinnerProcessando from '@/components/layout/spinnerProcessamento/SpinnerProcessamento.vue'
import TelefoneInput from '@/components/layout/telefone/TelefoneInput'

const SITUACAO = {
  emitenteJaCadastrado: 'emitente_ja_cadastrado',
  emitenteInativo: 'emitente_inativo'
}

const STATUS_SINCRONIZACAO = {
  vinculado: 'vinculado',
  erro: 'erro'
}

const OPCOES_CONFIRMACAO = {
  size: 'sm',
  buttonSize: 'sm',
  okTitle: 'Sim',
  okVariant: 'outline-success',
  cancelTitle: 'Não',
  cancelVariant: 'outline-primary',
  hideHeaderClose: true,
  centered: true
}

const CAMPOS_POR_ABA = {
  emitente: ['razao_social', 'cnpj', 'nome_fantasia', 'cpf', 'inscricao_municipal', 'inscricao_estadual', 'regime_tributario', 'incentivador_cultural', 'optante_simples_nacional', 'iss_retido'],
  servico: ['codigo_tributacao_nacional_issqn', 'codigo_cnae', 'regime_especial_tributacao_municipal'],
  endereco: ['cep', 'logradouro', 'numero', 'bairro', 'municipio', 'uf', 'codigo_municipio_ibge'],
  contato: ['telefone', 'email'],
  tributos: ['percentual_tributos_federais', 'percentual_tributos_estaduais', 'percentual_tributos_municipais'],
  config: ['cpf_cnpj_contabilidade', 'certificado', 'senha_certificado', 'logotipo'],
  responsavel: ['nome_responsavel', 'cpf_responsavel']
}

const emitentesMixins = {
  components: {
    spinnerProcessando,
    TelefoneInput
  },

  data () {
    return {
      dadosRetorno: {
        sucesso: false,
        mensagem: '',
        listaDeErros: {}
      },
      arquivos: {
        certificado: null,
        logotipo: null
      },
      removerLogotipo: false,
      verificacaoDocumento: {
        documento: '',
        situacao: '',
        mensagem: '',
        emitenteExistente: null
      },
      comboUf: [],
      flagAbas: {
        emitente: false,
        servico: false,
        endereco: false,
        contato: false,
        tributos: false,
        config: false,
        responsavel: false
      }
    }
  },

  mounted () {
    this.obterUnidadesFederativas()
  },

  computed: {
    ...mapState('emitentes', ['formulario', 'flagsDeControle']),

    desabilitarCampos () {
      return this.flagsDeControle.manipulandoDados.status || this.flagsDeControle.carregandoDados.status
    },

    ehPessoaFisica () {
      return Number(this.formulario.emitente.tipo) === 1
    },

    ehPessoaJuridica () {
      return Number(this.formulario.emitente.tipo) === 2
    },

    documentoBloqueado () {
      return this.formulario.emitente.possui_cadastro_fiscal === true
    },

    documentoInformado () {
      const documento = this.ehPessoaFisica ? this.formulario.emitente.cpf : this.formulario.emitente.cnpj
      return (documento || '').replace(/[.\-/\s]/g, '')
    },

    /** A situação só vale para o documento que a originou; trocar o documento a descarta. */
    verificacaoEhDoDocumentoAtual () {
      return this.verificacaoDocumento.documento === this.documentoInformado
    },

    /** Aviso do documento em digitação; sem verificação em curso, o resultado do último salvamento. */
    situacaoCadastroFiscal () {
      if (this.verificacaoEhDoDocumentoAtual && this.verificacaoDocumento.mensagem) {
        return this.verificacaoDocumento.mensagem
      }

      return this.situacaoDoUltimoSalvamento
    },

    erroMunicipio () {
      return this.dadosRetorno.listaDeErros.municipio || this.dadosRetorno.listaDeErros.codigo_municipio_ibge || ''
    },

    situacaoDoUltimoSalvamento () {
      const { status_sincronizacao: status, mensagem_sincronizacao: mensagem } = this.formulario.emitente

      if (status === STATUS_SINCRONIZACAO.vinculado) {
        return 'Este emitente está vinculado a um cadastro fiscal, mas o fornecedor ainda não liberou o ' +
          'token de emissão. Salve novamente para tentar novamente.'
      }

      if (status === STATUS_SINCRONIZACAO.erro) {
        return mensagem
          ? `A última tentativa de atualizar o cadastro fiscal não foi concluída: ${mensagem}`
          : 'A última tentativa de atualizar o cadastro fiscal não foi concluída. Salve novamente para reenviar.'
      }

      return ''
    }
  },

  methods: {
    ...mapActions('emitentes', ['ActionSalvarEmitente', 'ActionVerificarDocumentoEmitente']),

    mascaraCnpj (valor) {
      return obterMascaraCnpj(valor)
    },

    mascaraDocumentoIdentificacao (valor) {
      return mascaras.mascaraCpfCnpj(valor)
    },

    mascaraCpf (valor) {
      return mascaras.mascaraCpfCnpj(valor)
    },

    mascaraTelefone (valor) {
      return mascaras.mascaraTelefoneCelular(valor)
    },

    mascaraCep (valor) {
      return mascaras.mascaraCEP(valor)
    },

    async consultarCep () {
      const cep = (this.formulario.endereco.cep || '').replace(/\D/g, '')
      if (cep.length !== 8) return

      try {
        const resposta = await this.axios.get('/consultar/cep/' + cep)
        const dados = resposta.data?.retorno?.dados
        if (!dados) return

        this.formulario.endereco.logradouro = dados.logradouro ?? this.formulario.endereco.logradouro
        this.formulario.endereco.bairro = dados.bairro ?? this.formulario.endereco.bairro
        this.formulario.endereco.municipio = dados.cidade ?? this.formulario.endereco.municipio
        this.formulario.endereco.uf = dados.uf ?? this.formulario.endereco.uf
        this.formulario.endereco.codigo_ibge = dados.codigo_municipio_ibge ?? this.formulario.endereco.codigo_ibge
      } catch (e) {
        console.error(e)
      }
    },

    async obterUnidadesFederativas () {
      try {
        const resposta = await this.axios.post('/unidades-federativas', {})
        this.comboUf = resposta.data?.retorno?.dados ?? []
      } catch (e) {
        console.error(e)
      }
    },

    receberCertificado (arquivo) {
      this.arquivos.certificado = arquivo
    },

    receberLogotipo (blob) {
      this.arquivos.logotipo = blob
      this.removerLogotipo = false
    },

    alternarRemocaoLogotipo (valor) {
      this.removerLogotipo = valor
      if (valor) this.arquivos.logotipo = null
    },

    async verificarDocumento () {
      const documento = this.documentoInformado
      this.limparVerificacaoDocumento()

      if (!documento) return

      try {
        const retorno = await this.ActionVerificarDocumentoEmitente(documento)

        this.verificacaoDocumento = {
          documento,
          situacao: retorno?.dados?.situacao ?? '',
          mensagem: retorno?.mensagem ?? '',
          emitenteExistente: retorno?.dados?.emitente_existente ?? null
        }

        this.oferecerCadastroExistente()
      } catch (e) {
        console.error(e)
      }
    },

    limparVerificacaoDocumento () {
      this.verificacaoDocumento = { documento: '', situacao: '', mensagem: '', emitenteExistente: null }
    },

    oferecerCadastroExistente () {
      const { situacao, mensagem, emitenteExistente } = this.verificacaoDocumento
      const jaCadastrado = [SITUACAO.emitenteJaCadastrado, SITUACAO.emitenteInativo].includes(situacao)

      if (!jaCadastrado || !emitenteExistente || emitenteExistente.id === this.formulario.emitente.id) {
        return
      }

      this.$bvModal.msgBoxConfirm(`${mensagem} Deseja abrir o cadastro existente?`, OPCOES_CONFIRMACAO)
        .then(confirmou => {
          if (confirmou) {
            this.$router.push({ name: 'edicao-emitente', params: { id: emitenteExistente.id } })
          }
        })
    },

    async salvarEmitente () {
      if (this.desabilitarCampos) return

      this.dadosRetorno.listaDeErros = {}
      this.resetarFlagAbas()

      try {
        const data = await this.ActionSalvarEmitente(this.montarObjetoDeEnvio())
        await this.tratarRespostaSalvar(data)
      } catch (e) {
        const mensagemBackend = e.response?.data?.retorno?.mensagem ?? e.response?.data?.message
        util.criarAlertaToastDeErro(this, 'Erro', mensagemBackend || mensagens.falhaServidorSalvandoDados)
        console.error(e)
      }
    },

    resetarFlagAbas () {
      for (const aba of Object.keys(CAMPOS_POR_ABA)) {
        this.flagAbas[aba] = false
      }
    },

    atualizarFlagAbasComErro () {
      const camposComErro = Object.keys(this.dadosRetorno.listaDeErros)
      for (const aba of Object.keys(CAMPOS_POR_ABA)) {
        this.flagAbas[aba] = CAMPOS_POR_ABA[aba].some(campo => camposComErro.includes(campo))
      }
    },

    async tratarRespostaSalvar (data) {
      this.dadosRetorno = data?.retorno ?? { sucesso: false, mensagem: '', listaDeErros: {} }
      this.atualizarFlagAbasComErro()
      const aviso = util.obterDadosAlertaAposSalvarRegistro(this.dadosRetorno)

      if (aviso.exibir) {
        util.criarAlertaToastDeErro(this, 'Aviso', aviso.mensagem)
        return
      }

      this.$router.push('/emitentes').then(() => {
        util.criarAlertaToastDeSucesso(this, 'Aviso', this.dadosRetorno.mensagem)
      })
    },

    montarObjetoDeEnvio () {
      const f = this.formulario
      const dados = {
        id: f.emitente.id,
        razao_social: f.emitente.razao_social,
        // Envia só o documento pertinente ao tipo; nome_fantasia é exclusivo de PJ.
        nome_fantasia: this.ehPessoaJuridica ? f.emitente.nome_fantasia : null,
        cnpj: this.ehPessoaJuridica ? f.emitente.cnpj : null,
        cpf: this.ehPessoaFisica ? f.emitente.cpf : null,
        inscricao_municipal: f.emitente.inscricao_municipal,
        inscricao_estadual: f.emitente.inscricao_estadual,
        regime_tributario: f.emitente.regime_tributario,
        incentivador_cultural: f.emitente.incentivador_cultural,
        optante_simples_nacional: f.emitente.optante_simples_nacional,
        iss_retido: f.emitente.iss_retido,
        codigo_tributacao_nacional_issqn: f.servico.codigo_issqn,
        codigo_cnae: f.servico.cnae,
        regime_especial_tributacao_municipal: f.servico.regime_especial,
        cep: f.endereco.cep,
        logradouro: f.endereco.logradouro,
        numero: f.endereco.numero,
        complemento: f.endereco.complemento,
        bairro: f.endereco.bairro,
        municipio: f.endereco.municipio,
        uf: f.endereco.uf,
        codigo_municipio_ibge: f.endereco.codigo_ibge,
        telefone: f.contato.telefone,
        email: f.contato.email,
        percentual_tributos_federais: f.tributos.federais,
        percentual_tributos_estaduais: f.tributos.estaduais,
        percentual_tributos_municipais: f.tributos.municipais,
        cpf_cnpj_contabilidade: f.configuracoes.cpf_cnpj_contabilidade,
        senha_certificado: f.configuracoes.senha_certificado,
        recibo_danfe: f.configuracoes.recibo_danfe,
        exibe_sempre_ipi_danfe: f.configuracoes.exibe_sempre_ipi_danfe,
        exibe_issqn_danfe: f.configuracoes.exibe_issqn_danfe,
        exibe_impostos_adicionais_danfe: f.configuracoes.exibe_impostos_adicionais_danfe,
        exibe_rastro_danfe: f.configuracoes.exibe_rastro_danfe,
        exibe_unidade_tributaria_danfe: f.configuracoes.exibe_unidade_tributaria_danfe,
        exibe_sempre_volumes_danfe: f.configuracoes.exibe_sempre_volumes_danfe,
        ativo: f.configuracoes.ativo,
        habilita_nfsen_producao: f.configuracoes.habilita_nfsen_producao,
        habilita_nfsen_homologacao: f.configuracoes.habilita_nfsen_homologacao,
        nome_responsavel: f.responsavel.nome_responsavel,
        cpf_responsavel: f.responsavel.cpf_responsavel,
        login_responsavel: f.responsavel.login_responsavel,
        senha_responsavel: f.responsavel.senha_responsavel
      }

      const formData = new FormData()
      for (const [chave, valor] of Object.entries(dados)) {
        if (!util.valorEhNulo(valor)) formData.append(chave, valor)
      }

      if (!util.valorVazio(this.arquivos.certificado)) {
        formData.append('certificado', this.arquivos.certificado, this.arquivos.certificado.name)
      }
      if (!util.valorVazio(this.arquivos.logotipo)) {
        formData.append('logotipo', this.arquivos.logotipo, this.arquivos.logotipo.name ?? 'logotipo.png')
      }
      if (this.removerLogotipo) {
        formData.append('delete_logo', true)
      }

      return formData
    }
  }
}

export { emitentesMixins }
